import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Target,
  Users,
  Presentation,
  Calculator,
  Database,
  Send,
} from "lucide-react";

const Fundraising = () => {
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    startupName: "",
    fundingStage: "",
    fundingRequirement: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setIsSubmitting(true);

    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event: "fundraising_form_submit",
      form_name: "fundraising_landing_page",
      form_location: "fundraising_page",
    });

    try {
      const response = await fetch(
        "https://formsubmit.co/ajax/singh.arshdeep1084@gmail.com",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            name: formData.name,
            email: formData.email,
            phone: formData.phone,
            startup_name: formData.startupName,
            funding_stage: formData.fundingStage,
            funding_requirement: formData.fundingRequirement,
            _subject:
              "New FounderScale Capital Fundraising Enquiry",
            _template: "table",
            _url:
              "https://founderscalecapital.com/fundraising",
          }),
        }
      );

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error("Form submission failed");
      }

      window.dataLayer.push({
        event: "lead_form_submission",
        form_name: "fundraising_landing_page",
        form_location: "fundraising_page",
      });

      window.dataLayer.push({
        event: "fundraising_form_success",
        form_name: "fundraising_landing_page",
        form_location: "fundraising_page",
      });

      navigate("/thank-you");

    } catch (err) {
      console.error("Fundraising form error:", err);

      setError(
        "Something went wrong while submitting the form. Please try again."
      );

      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">

      {/* ================================================= */}
      {/* HERO + FORM */}
      {/* ================================================= */}

      <section className="relative overflow-hidden bg-gradient-to-br from-emerald-700 via-emerald-600 to-green-600 px-6 py-12 text-white md:py-16">

        <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full bg-white/10" />

        <div className="absolute -bottom-40 -left-32 h-96 w-96 rounded-full bg-white/10" />

        <div className="relative mx-auto max-w-6xl">

          <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-16">

            {/* LEFT SIDE */}

            <div>

              <div className="mb-5 inline-flex items-center rounded-full bg-white/15 px-5 py-2 text-sm font-bold">
                FounderScaleCapital
              </div>

              <h1 className="mb-5 text-4xl font-bold leading-tight md:text-5xl lg:text-6xl">
                Raise Capital With the Right Strategy, Investors & Execution
              </h1>

              <p className="mb-7 max-w-xl text-lg leading-relaxed text-emerald-50">
                We help startups prepare for fundraising, identify relevant
                investors, build investor-ready materials and execute
                structured investor outreach.
              </p>

              <div className="space-y-3">

                <div className="flex items-center gap-3">
                  <CheckCircle2
                    size={21}
                    className="flex-shrink-0 text-white"
                  />
                  <span className="text-sm text-emerald-50 md:text-base">
                    Fundraising strategy and preparation
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <CheckCircle2
                    size={21}
                    className="flex-shrink-0 text-white"
                  />
                  <span className="text-sm text-emerald-50 md:text-base">
                    Relevant investor discovery and mapping
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <CheckCircle2
                    size={21}
                    className="flex-shrink-0 text-white"
                  />
                  <span className="text-sm text-emerald-50 md:text-base">
                    Structured investor outreach
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <CheckCircle2
                    size={21}
                    className="flex-shrink-0 text-white"
                  />
                  <span className="text-sm text-emerald-50 md:text-base">
                    Pitch deck and financial model support
                  </span>
                </div>

              </div>

              <div className="mt-8">

                <a
                  href="/"
                  className="inline-flex items-center gap-2 text-sm font-semibold text-white underline-offset-4 hover:underline"
                >
                  Want to know more about us?
                  <span>Explore FounderScaleCapital</span>
                  <ArrowRight size={16} />
                </a>

              </div>

            </div>


            {/* FORM */}

            <div
              id="fundraising-form"
              className="rounded-2xl bg-white p-6 text-slate-900 shadow-2xl md:p-8"
            >

              <div className="mb-6">

                <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-emerald-600">
                  Start Your Fundraise
                </p>

                <h2 className="mb-2 text-2xl font-bold md:text-3xl">
                  Tell Us About Your Fundraise
                </h2>

                <p className="text-sm leading-relaxed text-slate-500">
                  Share a few details and our team will get in touch with you.
                </p>

              </div>

              <form
                onSubmit={handleSubmit}
                className="space-y-4"
              >

                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                    Your Name
                  </label>

                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Enter your name"
                    className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                    Email Address
                  </label>

                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="you@example.com"
                    className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                    Phone Number
                  </label>

                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+91 XXXXX XXXXX"
                    className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                    Startup / Company Name
                  </label>

                  <input
                    type="text"
                    name="startupName"
                    value={formData.startupName}
                    onChange={handleChange}
                    required
                    placeholder="Enter startup name"
                    className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                    Funding Stage
                  </label>

                  <select
                    name="fundingStage"
                    value={formData.fundingStage}
                    onChange={handleChange}
                    required
                    className="w-full rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                  >
                    <option value="">
                      Select funding stage
                    </option>

                    <option value="Pre-Seed">
                      Pre-Seed
                    </option>

                    <option value="Seed">
                      Seed
                    </option>

                    <option value="Pre-Series A">
                      Pre-Series A
                    </option>

                    <option value="Series A">
                      Series A
                    </option>

                    <option value="Series B+">
                      Series B+
                    </option>

                    <option value="Other">
                      Other
                    </option>
                  </select>
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                    Funding Requirement
                  </label>

                  <input
                    type="text"
                    name="fundingRequirement"
                    value={formData.fundingRequirement}
                    onChange={handleChange}
                    required
                    placeholder="e.g. ₹2 Crore"
                    className="w-full rounded-lg border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                  />
                </div>

                {error && (
                  <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-600">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="group flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-6 py-3.5 text-sm font-bold text-white shadow-lg shadow-emerald-600/20 transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSubmitting ? (
                    "Submitting..."
                  ) : (
                    <>
                      Get Fundraising Support
                      <ArrowRight
                        size={18}
                        className="transition-transform group-hover:translate-x-1"
                      />
                    </>
                  )}
                </button>

                <p className="text-center text-xs leading-relaxed text-slate-400">
                  Your information will only be used to contact you regarding
                  your fundraising requirements.
                </p>

              </form>

            </div>

          </div>

        </div>

      </section>


      {/* ================================================= */}
      {/* WHAT WE HELP WITH */}
      {/* ================================================= */}

      <section className="px-6 py-16 md:py-20">

        <div className="mx-auto max-w-6xl">

          <div className="mb-12 text-center">

            <p className="mb-3 text-sm font-bold uppercase tracking-[0.18em] text-emerald-600">
              What We Help With
            </p>

            <h2 className="text-3xl font-bold text-slate-900 md:text-4xl">
              A Structured Fundraising Process
            </h2>

            <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
              From fundraising preparation to investor outreach, we help
              founders build a structured path towards their capital raise.
            </p>

          </div>

          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">

            {[
              {
                icon: Target,
                title: "Fundraising Strategy",
                text: "Build a clear fundraising strategy aligned with your startup's stage, goals and capital requirement.",
              },
              {
                icon: Users,
                title: "Investor Discovery",
                text: "Identify relevant investors based on your sector, stage, geography and fundraising requirement.",
              },
              {
                icon: Send,
                title: "Investor Outreach",
                text: "Execute structured outreach to relevant investors and create a systematic fundraising pipeline.",
              },
              {
                icon: Presentation,
                title: "Pitch Deck Support",
                text: "Strengthen your investor presentation and communicate your opportunity clearly.",
              },
              {
                icon: Calculator,
                title: "Financial Modelling",
                text: "Develop investor-ready financial projections and fundraising models.",
              },
              {
                icon: Database,
                title: "Data Room Preparation",
                text: "Organise the key information and documents investors need during their evaluation.",
              },
            ].map((item, index) => {

              const Icon = item.icon;

              return (
                <div
                  key={index}
                  className="rounded-2xl border border-slate-200 bg-white p-7 shadow-sm transition hover:-translate-y-1 hover:shadow-lg"
                >

                  <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-100">

                    <Icon
                      size={24}
                      className="text-emerald-600"
                    />

                  </div>

                  <h3 className="mb-3 text-xl font-bold text-slate-900">
                    {item.title}
                  </h3>

                  <p className="text-sm leading-relaxed text-slate-600">
                    {item.text}
                  </p>

                </div>
              );

            })}

          </div>

        </div>

      </section>


      {/* ================================================= */}
      {/* WHO WE WORK WITH */}
      {/* ================================================= */}

      <section className="bg-gradient-to-br from-emerald-50 via-green-50 to-white px-6 py-16 md:py-20">

        <div className="mx-auto max-w-5xl">

          <div className="grid gap-10 md:grid-cols-2 md:items-center">

            <div>

              <p className="mb-3 text-sm font-bold uppercase tracking-[0.18em] text-emerald-600">
                Who We Work With
              </p>

              <h2 className="mb-5 text-3xl font-bold text-slate-900 md:text-4xl">
                Built for Startups Preparing to Raise
              </h2>

              <p className="mb-6 text-base leading-relaxed text-slate-600">
                Whether you are preparing for your first institutional round
                or looking to raise your next stage of capital, our fundraising
                support is designed to help you approach the process with
                greater structure and clarity.
              </p>

              <div className="space-y-3">

                {[
                  "Pre-Seed startups",
                  "Seed-stage companies",
                  "Pre-Series A startups",
                  "Series A and growth-stage companies",
                ].map((item) => (

                  <div
                    key={item}
                    className="flex items-center gap-3"
                  >

                    <CheckCircle2
                      size={20}
                      className="flex-shrink-0 text-emerald-600"
                    />

                    <span className="font-medium text-slate-700">
                      {item}
                    </span>

                  </div>

                ))}

              </div>

            </div>


            <div className="rounded-2xl bg-white/80 p-8 shadow-sm ring-1 ring-emerald-100 md:p-10">

              <h3 className="mb-4 text-2xl font-bold text-slate-900">
                Not sure where to start?
              </h3>

              <p className="mb-6 leading-relaxed text-slate-600">
                Tell us about your startup and fundraising requirement.
                Our team can understand your situation and help you identify
                the right next steps.
              </p>

              <a
                href="#fundraising-form"
                className="inline-flex items-center gap-2 font-bold text-emerald-700 hover:text-emerald-800"
              >
                Discuss Your Fundraise
                <ArrowRight size={18} />
              </a>

            </div>

          </div>

        </div>

      </section>


      {/* ================================================= */}
      {/* FINAL CTA */}
      {/* ================================================= */}

      <section className="px-6 py-16 text-center md:py-20">

        <div className="mx-auto max-w-3xl">

          <h2 className="mb-5 text-3xl font-bold text-slate-900 md:text-4xl">
            Want to Learn More About FounderScaleCapital?
          </h2>

          <p className="mb-8 text-lg leading-relaxed text-slate-600">
            Explore our services, approach and resources before starting
            your fundraising conversation.
          </p>

          <a
            href="/"
            className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-7 py-4 text-sm font-bold text-white transition hover:bg-slate-800"
          >
            Explore FounderScaleCapital
            <ArrowRight size={18} />
          </a>

        </div>

      </section>

    </div>
  );
};

export default Fundraising;
